import { __awaiter } from "tslib";
import { AbortController } from 'src/global/abort-controller/AbortController';
import WebSocket from 'isomorphic-ws';
import { WebSocketError } from 'src/services/api/common/ws/WebSocketError';
import { delay } from 'src/global/delay';
import { combineAbortSignals, useAbortController } from 'src/global/abort-controller/helpers';
import { jsonStringifyOnServer } from 'src/global/jsonStringifyOnServer';
import { webSocketClose } from 'src/services/api/common/ws/helpers';
export { WebSocket };
export const TIMEOUT_DEFAULT = 60000;
export function generateRequestId() {
    return Date.now().toString(36)
        + '-' + Math.ceil(Math.random() * Number.MAX_SAFE_INTEGER).toString(36)
        + '-' + Math.ceil(Math.random() * Number.MAX_SAFE_INTEGER).toString(36);
}
function createTimeoutAbortSignal(timeout, signal) {
    if (signal && signal.aborted) {
        return null;
    }
    if (!timeout) {
        throw new WebSocketError('timeout is not defined', 'internal-error');
    }
    const abortController = new AbortController();
    const timer = setTimeout(() => {
        abortController.abort();
    }, timeout);
    if (signal) {
        signal.addEventListener('abort', () => {
            clearTimeout(timer);
        });
    }
    return abortController.signal;
}
function webSocketSubscribe(createClient, { responseDataFromString, responsePredicate, validate, getRequestId, delayAfterError, maxDelayAfterError, }, callback) {
    if (!delayAfterError) {
        delayAfterError = 1000;
    }
    if (!maxDelayAfterError) {
        maxDelayAfterError = 60000;
    }
    const abortController = new AbortController();
    let _unsubscribe;
    abortController.signal.addEventListener('abort', () => {
        if (_unsubscribe) {
            _unsubscribe();
            _unsubscribe = null;
        }
    });
    function subscribe() {
        let _client;
        try {
            _client = createClient();
            if (abortController.signal.aborted) {
                return;
            }
            _unsubscribe = _webSocketSubscribe(_client, {
                responseDataFromString,
                responsePredicate,
                validate,
                getRequestId,
            }, (response, error) => __awaiter(this, void 0, void 0, function* () {
                if (_client.readyState >= WebSocket.CLOSING) {
                    if (_unsubscribe) {
                        _unsubscribe();
                        _unsubscribe = null;
                        yield delay(delayAfterError, abortController.signal);
                        delayAfterError *= 2;
                        if (delayAfterError > maxDelayAfterError) {
                            delayAfterError = maxDelayAfterError;
                        }
                        subscribe();
                    }
                    return;
                }
                callback(response, error);
            }));
        }
        catch (err) {
            abortController.abort();
            callback(null, err);
            return;
        }
    }
    subscribe();
    return () => {
        abortController.abort();
    };
}
function webSocketSend(createClient, { data, requestDataToString, setRequestId, requestId, }, callback) {
    function send() {
        let _client = createClient();
        _webSocketSend(_client, {
            data,
            requestDataToString,
            setRequestId,
            requestId,
        }, (error) => {
            if (_client.readyState >= WebSocket.CLOSING) {
                send();
                return;
            }
            if (error && callback) {
                callback(error);
            }
        });
    }
    send();
}
function webSocketRequest(createClient, { data, requestDataToString, responseDataFromString, setRequestId, getRequestId, requestId, validate, abortSignal, timeout, delayAfterError, maxDelayAfterError, }) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!delayAfterError) {
            delayAfterError = 1000;
        }
        if (!maxDelayAfterError) {
            maxDelayAfterError = 60000;
        }
        return useAbortController((_abortSignal) => __awaiter(this, void 0, void 0, function* () {
            const combinedAbortSignals = combineAbortSignals(abortSignal, _abortSignal);
            const timeoutSignal = createTimeoutAbortSignal(timeout, combinedAbortSignals);
            let _client = createClient();
            while (true) {
                try {
                    // eslint-disable-next-line no-await-in-loop
                    return yield _webSocketRequest(_client, {
                        data,
                        requestDataToString,
                        responseDataFromString,
                        setRequestId,
                        getRequestId,
                        requestId,
                        validate,
                        abortSignal: combinedAbortSignals,
                        timeoutSignal,
                    });
                }
                catch (err) {
                    if (combinedAbortSignals && combinedAbortSignals.aborted
                        || timeoutSignal && timeoutSignal.aborted
                        || _client.readyState === WebSocket.OPEN) {
                        throw err;
                    }
                    yield delay(delayAfterError, combinedAbortSignals);
                    delayAfterError *= 2;
                    if (delayAfterError > maxDelayAfterError) {
                        delayAfterError = maxDelayAfterError;
                    }
                }
            }
        }));
    });
}
function _webSocketSubscribe(client, { responseDataFromString, responsePredicate, validate, getRequestId, }, callback) {
    function unsubscribe() {
        client.removeEventListener('message', onMessage);
        client.removeEventListener('close', onClose);
    }
    function onMessage(e) {
        receiveData(e.data, e.handled);
        e.handled = true;
    }
    function onClose(e) {
        unsubscribe();
        callback(null, new WebSocketError(`WebSocket closed; code: ${e.code}; reason: ${e.reason}`, 'closed-' + e.code));
    }
    function receiveData(responseDataStr, handled) {
        let responseData;
        try {
            responseData = responseDataFromString(responseDataStr);
            // if (process.env.SHOW_BROWSER_LOG) {
            // console.debug('RECEIVE: ' + JSON.stringify(responseData, null, 4))
            // }
            if (!handled && process.env.SHOW_BROWSER_LOG) {
                console.debug('RECEIVE: ', jsonStringifyOnServer(responseData));
            }
            if (responsePredicate) {
                const responseRequestId = getRequestId(responseData);
                if (!responsePredicate(responseData, responseRequestId)) {
                    return;
                }
            }
        }
        catch (err) {
            if (process.env.SHOW_BROWSER_LOG) {
                console.warn(err); // TODO: delete this
            }
            return;
        }
        try {
            const validateError = validate && validate(responseData);
            if (validateError) {
                callback(null, new WebSocketError(validateError.message || 'Invalid response: ' + responseDataStr, validateError.code, responseData));
                return;
            }
        }
        catch (err) {
            callback(null, err);
            return;
        }
        callback(responseData, null);
    }
    if (client.readyState >= WebSocket.CLOSING) {
        callback(null, new WebSocketError('WebSocket.readyState == ' + client.readyState, 'ready-state-' + client.readyState));
    }
    else {
        client.addEventListener('message', onMessage);
        client.addEventListener('close', onClose);
    }
    return unsubscribe;
}
function _webSocketSend(client, { data, requestDataToString, setRequestId, requestId, }, callback) {
    if (!requestId) {
        requestId = generateRequestId();
    }
    data = setRequestId(data, requestId);
    const dataStr = requestDataToString(data);
    if (process.env.SHOW_BROWSER_LOG) {
        // console.debug('SEND: ' + JSON.stringify(JSON.parse(dataStr), null, 4)
        console.debug('SEND: ', jsonStringifyOnServer(JSON.parse(dataStr)));
    }
    function unsubscribe() {
        client.removeEventListener('open', onOpen);
        client.removeEventListener('close', onClose);
    }
    function onClose(e) {
        unsubscribe();
        if (callback) {
            callback(new WebSocketError(`WebSocket closed; code: ${e.code}; reason: ${e.reason}`, 'closed-' + e.code));
        }
    }
    function onOpen(e) {
        unsubscribe();
        sendData();
    }
    function sendData() {
        client.send(dataStr);
        if (callback) {
            callback(null);
        }
    }
    if (client.readyState < WebSocket.CLOSING) {
        if (client.readyState === WebSocket.CONNECTING) {
            client.addEventListener('open', onOpen);
            client.addEventListener('close', onClose);
        }
        else {
            sendData();
        }
    }
    return requestId;
}
function _webSocketRequest(client, { data, requestDataToString, responseDataFromString, setRequestId, getRequestId, requestId, validate, abortSignal, timeoutSignal, }) {
    return new Promise((resolve, reject) => {
        let _requestId;
        const _unsubscribe = _webSocketSubscribe(client, {
            responseDataFromString,
            validate,
            responsePredicate(responseData, responseRequestId) {
                return responseRequestId === _requestId;
            },
            getRequestId,
        }, (responseData, error) => {
            if (error) {
                _reject(error);
                return;
            }
            _resolve(responseData);
        });
        function unsubscribe() {
            if (abortSignal) {
                abortSignal.removeEventListener('abort', onAbort);
            }
            if (timeoutSignal) {
                timeoutSignal.removeEventListener('abort', onTimeout);
            }
            if (_unsubscribe) {
                _unsubscribe();
            }
        }
        function _resolve(responseData) {
            unsubscribe();
            resolve(responseData);
        }
        function _reject(error) {
            unsubscribe();
            reject(error);
        }
        function onAbort() {
            _reject(new WebSocketError('WebSocket request aborted', 'aborted'));
        }
        function onTimeout() {
            _reject(new WebSocketError('WebSocket request aborted by timeout', 'timeout'));
        }
        if (abortSignal) {
            if (abortSignal.aborted) {
                onAbort();
                return;
            }
            abortSignal.addEventListener('abort', onAbort);
        }
        if (timeoutSignal) {
            if (timeoutSignal.aborted) {
                onTimeout();
                return;
            }
            timeoutSignal.addEventListener('abort', onTimeout);
        }
        _requestId = _webSocketSend(client, {
            data,
            setRequestId,
            requestDataToString,
            requestId,
        });
    });
}
export class WebSocketApi {
    constructor({ url, requestParamsDefault, }) {
        this._abortController = new AbortController();
        this.url = url;
        this.client = null;
        this.requestParamsDefault = requestParamsDefault;
        this._onUnexpectedClose = this._onUnexpectedClose.bind(this);
        this._onError = this._onError.bind(this);
        this._open();
    }
    _onUnexpectedClose(e) {
        if (process.env.SHOW_BROWSER_LOG) {
            console.warn(`WebSocket unexpectedly closed; code=${e.code}; reason=${e.reason}`);
        }
        this._open();
    }
    _onError(e) {
        console.warn('WebSocket error: ', e);
        this._unsubscribe();
    }
    _open() {
        this._unsubscribe();
        this.client = new WebSocket(this.url);
        this.client.addEventListener('close', this._onUnexpectedClose);
        this.client.addEventListener('error', this._onError);
    }
    _unsubscribe() {
        if (this.client) {
            this.client.removeEventListener('close', this._onUnexpectedClose);
            this.client.removeEventListener('error', this._onError);
        }
    }
    subscribe({ responseDataFromString, responsePredicate, validate, getRequestId, }, callback) {
        return webSocketSubscribe(() => {
            if (this.client.readyState >= WebSocket.CLOSING) {
                this._open();
            }
            return this.client;
        }, {
            responseDataFromString: responseDataFromString || this.requestParamsDefault.responseDataFromString,
            responsePredicate,
            validate: validate || this.requestParamsDefault.validate,
            getRequestId: getRequestId || this.requestParamsDefault.getRequestId,
        }, callback);
    }
    send({ data, requestDataToString, setRequestId, requestId, }, callback) {
        webSocketSend(() => {
            if (this.client.readyState >= WebSocket.CLOSING) {
                this._open();
            }
            return this.client;
        }, {
            data,
            requestDataToString: requestDataToString || this.requestParamsDefault.requestDataToString,
            setRequestId: setRequestId || this.requestParamsDefault.setRequestId,
            requestId,
        }, callback);
    }
    request(args) {
        return webSocketRequest(() => {
            if (this.client.readyState >= WebSocket.CLOSING) {
                this._open();
            }
            return this.client;
        }, Object.assign(Object.assign(Object.assign({}, this.requestParamsDefault), args), { abortSignal: combineAbortSignals(args.abortSignal, this._abortController.signal), timeout: args.timeout || TIMEOUT_DEFAULT }));
    }
    close() {
        return __awaiter(this, void 0, void 0, function* () {
            this.client.removeEventListener('close', this._onUnexpectedClose);
            this._unsubscribe();
            yield webSocketClose(this.client);
        });
    }
    destroy() {
        return __awaiter(this, void 0, void 0, function* () {
            this._abortController.abort();
            yield this.close();
            this.client = null;
        });
    }
}
